import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Title } from "rbx";
import { useApolloClient, useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import { ReactSelect, Loader } from "../../../../components";
import { GET_USERS } from "../../../Reviews/graphql";
import { UPDATE_REVIEW_MANAGER } from "../../graphql";
import capitalizeEveryWord from "../../../../utils/capitalizeEveryWord";

const UpdateReviewManagerModal = ({ review, onComplete }) => {
  const [options, setOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedManager, setSelectedManager] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const client = useApolloClient();

  const [updateReviewManager] = useMutation(UPDATE_REVIEW_MANAGER);

  const currentManagerEmployeeNumber = review?.managerEmployeeNumber || "";
  const missingEmployeeNumber = !currentManagerEmployeeNumber;

  const isSameManager =
    selectedManager?.value &&
    currentManagerEmployeeNumber &&
    String(selectedManager.value).trim() ===
      String(currentManagerEmployeeNumber).trim();

  const isDisabled = !selectedManager?.value || isSameManager || submitting;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const { data } = await client.query({
          query: GET_USERS,
          variables: {
            where: { employmentStatus: { equals: "active" } },
            orderBy: [{ firstName: "asc" }],
          },
          fetchPolicy: "network-only",
        });

        const dropdownOptions =
          data?.users?.map((user) => ({
            label: capitalizeEveryWord(
              `${user?.firstName?.trim()} ${user?.lastName?.trim()}`
            ),
            value: user?.employeeNumber,
          })) || [];

        setOptions(
          dropdownOptions.sort((a, b) => a.label.localeCompare(b.label))
        );
      } catch (error) {
        console.error(error);
        toast.error("Could not load users. Please try again.");
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [client]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isDisabled) return;

    setSubmitting(true);
    try {
      await updateReviewManager({
        variables: {
          reviewId: review.id,
          oldManagerEmployeeNumber: currentManagerEmployeeNumber || "",
          newManagerEmployeeNumber: selectedManager.value,
          oldManagerName: review.manager || null,
        },
      });

      await client.refetchQueries({ include: ["Reviews"] });

      toast.success("Manager updated successfully.");
      onComplete();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.message || "Failed to update manager. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const currentManagerLabel = useMemo(
    () => review?.manager || "Unknown",
    [review?.manager]
  );

  if (loadingUsers || submitting) {
    return (
      <div style={{ minHeight: "10rem" }}>
        <div className="loader-container">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <header className="modal-card-head">
        <div className="modal-card-title">
          <Title size={4}>Update Manager</Title>
        </div>
        <div>
          <Button.Group>
            <Button
              color="primary"
              disabled={isDisabled}
              size="small"
              style={{ marginBottom: "0.5rem" }}
              type="submit"
            >
              <span>Save and Close</span>
            </Button>
          </Button.Group>
        </div>
      </header>
      <hr />
      <p style={{ marginBottom: "1rem" }}>
        <strong>Review:</strong> {review?.title}
      </p>
      <p style={{ marginBottom: "1rem" }}>
        <strong>Current manager:</strong> {currentManagerLabel}
      </p>
      {missingEmployeeNumber && (
        <p style={{ color: "#b45309", marginBottom: "1rem" }}>
          Current manager employee number is missing; matching will use the
          manager name only.
        </p>
      )}
      <div style={{ marginBottom: "1rem" }}>
        <Title size={6} style={{ marginBottom: "0.5rem" }}>
          Select new manager
        </Title>
        <ReactSelect
          closeMenuOnSelect
          isMulti={false}
          name="manager"
          options={options}
          placeholder="Select manager"
          value={selectedManager}
          onChange={(selected) => setSelectedManager(selected)}
        />
      </div>
      {isSameManager && (
        <span style={{ color: "red" }}>
          Selected user is already the current manager.
        </span>
      )}
    </form>
  );
};

UpdateReviewManagerModal.propTypes = {
  review: PropTypes.object.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default UpdateReviewManagerModal;
